<template lang="pug">
  #challenge-add-participants
    WtTitle(h2) Step 3: Participants

    WtTile(parent vertical)
      WtTile(child child-class="")
        Questions(@actionSelected="newAction => this.action = newAction")
        template(v-if="includeAllEntities")
          h3 {{ includeAllEntitiesNote }}
          br
        template(v-if="memberMode && actionTypes.includes(action)")
          BField(v-if="isOrganizationAdmin" label="Invite All Members From Your Organization" horizontal)
            WtButton(
              outlined
              color="primary"
              @click.native="inviteAllOrganizationMembers"
            )
              BIcon(slot="icon" icon="email-outline")
              span Invite All Organization Members
        template(v-if="memberMode && actionTypes.includes(action)")
          BField(v-if="isAdmin" :label="action === 'invite' ? 'Invite All Members Now:' : 'Add All Members Now:'" horizontal)
            WtButton(
              outlined
              color="primary"
              @click.native="action === 'invite' ? includeAll('members', 'invite') : includeAll('members', 'add')"
             )
              BIcon(slot="icon" icon="email-outline")
              span {{ includeAllEntitiesButtonText }}
          .level
          BField(:label="action === 'invite' ? 'Invite Select Members:' : 'Add Select Members:'" horizontal)
            WtButton(
              outlined
              color="primary"
              @click.native="toggleParticipantsTable"
            )
              BIcon(slot="icon" :icon="participantsTableOpen ? 'chevron-up' : 'chevron-down'")
              span Show Members
          ChallengeIncludeMembers(
            v-show="participantsTableOpen"
            title=""
            :tooltip-text="includeMembersTooltip"
            :open-toggle="participantsTableOpen"
            :loaded="membersLoaded"
            @search="onSearch($event)"
            @toggle="participantsTableOpen = $event"
            :includeByType="action"
          )

        template(v-if="teamMode && actionTypes.includes(action)")
          BField(v-if="isAdmin" :label="action === 'invite' ? 'Invite All Teams Now:' : 'Add All Teams Now:'" horizontal)
            WtButton(
              outlined
              color="primary"
              @click.native="action === 'invite' ? includeAll('teams', 'invite') : includeAll('teams', 'add')"
             )
              BIcon(slot="icon" icon="email-outline")
              span {{ includeAllEntitiesButtonText }}
          .level
          BField(:label="action === 'invite' ? 'Invite Select Teams:' : 'Add Select Teams:'" horizontal)
            WtButton(
              outlined
              color="primary"
              @click.native="toggleParticipantsTable"
            )
              BIcon(slot="icon" :icon="participantsTableOpen ? 'chevron-up' : 'chevron-down'")
              span Show Teams
          ChallengeIncludeTeams(
            v-show="participantsTableOpen"
            title=""
            :tooltip-text="includeTeamsTooltip"
            :open-toggle="participantsTableOpen"
            :loaded="true"
            @search="onSearch($event)"
            @toggle="participantsTableOpen = $event"
            :includeByType="action"
          )
        
        template(v-if="teamMode && this.$auth.user().isAdmin && action === 'add'")
          BField(v-if="isOrganizationOn && havingOrganizations" :label="'Add Organization To Challenge'" horizontal).pt-2
            BDropdown(
                  v-model="organizationId"
                  append-to-body
                  aria-role="menu"
                  scrollable
                  max-height="200"
                  trap-focus
                ).full-width-dropdown
                  .sort-by-trigger(slot="trigger" slot-scope="{ active }")
                    BButton(
                      :label="organizationId ? _.find(programOrganizations, { id :organizationId }).name : 'Select Organization'"
                      :icon-right="active ? 'chevron-up' : 'chevron-down'"
                    ).customize-button
                  template(
                    v-for="option in programOrganizations"
                    v-if="option.active"
                  )
                    BDropdownItem(
                      :value="option.id"
                      @click.native="addOrgnizationToChallenge(option)"
                    ) {{ option.name }}
                
    ChallengeBuilderControls(:step-to-activate="4" :allow-next="allowNext")
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";
import WtTile from "@@/WtTile/WtTile";
import Questions from "@@/ChallengeBuilder/ChallengeBuilderAddParticipantQuestions";
import ChallengeBuilderControls from "@@/ChallengeBuilder/ChallengeBuilderControls";
import ChallengeIncludeMembers from "@@/Challenge/ChallengeIncludeMembers";
import ChallengeIncludeTeams from "@@/Challenge/ChallengeIncludeTeams";

const INVITE = "invite";
const ADD = "add";
const SKIP = "skip";

/**
 * Challenge Builder wrapper for Step 3, inviting participants to challenge
 */
export default {
  name: "ChallengeAddParticipants",

  components: {
    ChallengeBuilderControls,
    Questions,
    WtButton,
    WtTitle,
    WtTile,
    ChallengeIncludeMembers,
    ChallengeIncludeTeams
  },

  data() {
    return {
      action: null,
      participantsTableOpen: false,
      actionTypes: [INVITE, ADD],
      includeByType: "",
      includeAllEntities: false,
      includeAllEntitiesNote: "",
      organizationId: null
    };
  },

  computed: {
    ...mapGetters("program", [
      "membersLoaded",
      "programOrganizations",
      "programOrganizationsLoaded",
      "isProTier",
      "coreProgramMeta"
    ]),
    ...mapGetters("authMember", ["isAdmin"]),
    ...mapGetters("challengeBuilder", [
      "stagedMembers",
      "stagedTeams",
      "stagedOrganizations",
      "memberMode",
      "teamMode"
    ]),
    ...mapGetters("members", ["member"]),

    allowNext() {
      const stagedThings = this.teamMode
        ? this.stagedTeams || this.stagedOrganizations
        : this.stagedMembers || this.stagedOrganizations;

      return (
        (this.actionTypes.includes(this.action) &&
          !this._.isEmpty(stagedThings)) ||
        this.action === SKIP ||
        this.includeAllEntities
      );
    },

    includeAllEntitiesButtonText() {
      return this.action === "invite" ? "INVITE ALL" : "ADD ALL";
    },

    isOrganizationOn() {
      return this.$auth.user().program.organizationsOn;
    },

    includeMembersTooltip() {
      return `Click here to include all members you'd like to ${this.action}, then click "save changes"`;
    },

    includeTeamsTooltip() {
      return `Click here to include all teams you'd like to ${this.includeByType}, then click "save changes"`;
    },

    isOrganizationAdmin() {
      return (
        this.member.id ===
        this._.get(this.member, "organizationData.adminId", 0)
      );
    }
  },

  watch: {
    action() {
      this.organizationId = null;
      this.clearSelectedEntities();
      this.includeAllEntities = false;
      this.participantsTableOpen = false;
      switch (this.action) {
        case INVITE:
          this.updateFormData({ key: "includeAll", value: 0 });
          this.updateFormData({ key: "competitionOf", value: "" });
          this.updateFormData({ key: "includeType", value: "invite" });
          break;
        case ADD:
          this.updateFormData({ key: "includeAll", value: 0 });
          this.updateFormData({ key: "competitionOf", value: "" });
          this.updateFormData({ key: "includeType", value: "add" });
          break;
        case SKIP:
          this.updateFormData({ key: "includeAll", value: 0 });
          this.updateFormData({ key: "competitionOf", value: "" });
          this.updateFormData({ key: "includeType", value: "" });
          break;
      }
    }
  },

  async mounted() {
    if (this.memberMode) {
      this.onSearch = this._.debounce(async query => {
        const response = await this.searchMembers({
          programId: this.$auth.user().programId,
          query: query,
          perPage: 10
        });

        this.setMembersResponseObject(response);
      }, 500);
    }

    if (this.isProTier) {
      if (this.isAdmin && !this.programOrganizationsLoaded) {
        this.fetchProgramOrganizations({
          programId: this.$auth.user().programId
        });
      } else {
        this.organizationId = this._.get(this.member, "organizationId", null);
      }
    }
  },

  methods: {
    ...mapGetters("challengeBuilder", ["formData", ""]),
    ...mapActions("organizations", ["fetchOrganizationTeams"]),
    ...mapActions("challengeBuilder", [
      "emptyStagedMembers",
      "emptyStagedTeams",
      "updateFormData",
      "stageOrganization",
      "emptyStagedOrganizations"
    ]),
    ...mapActions("program", [
      "searchMembers",
      "setMembersResponseObject",
      "fetchProgramOrganizations"
    ]),
    ...mapActions("challenges", ["addEntitiesToChallenge"]),

    havingOrganizations() {
      return (
        Array.isArray(this.programOrganizations) &&
        this.programOrganizations.length > 0
      );
    },

    toggleParticipantsTable() {
      this.clearSelectedEntities();
      this.includeAllEntities = false;
      this.participantsTableOpen = !this.participantsTableOpen;
    },

    clearSelectedEntities() {
      this.organizationId = null;
      if (this.memberMode) {
        this.emptyStagedMembers();
      }
      if (this.teamMode) {
        this.emptyStagedTeams();
      }
      this.emptyStagedOrganizations();
      this.updateFormData({ key: "includeAll", value: 0 });
      this.updateFormData({ key: "competitionOf", value: "" });
    },

    includeAll(entitiesType, action) {
      // entitiesType: string -> can be "members" or "teams"
      // action: string -> can be "add" or "invite"

      this.clearSelectedEntities();
      this.includeEntityTableOpen = false;

      const pastActionStr = action === "invite" ? "invited" : "added";
      this.$buefy.dialog.confirm({
        title:
          this._.upperFirst(action) +
          " All " +
          this._.upperFirst(entitiesType) +
          " To This Challenge",
        confirmText:
          this._.upperFirst(action) + " All " + this._.upperFirst(entitiesType),
        type: "is-danger",
        hasIcon: true,
        message: `Are you sure you want to ${action} all ${entitiesType} to this Challenge?`,
        onConfirm: async () => {
          this.updateFormData({ key: "includeAll", value: 1 });
          this.includeAllEntitiesNote = `✔ Awesome, all program ${entitiesType} will be ${pastActionStr} to this challenge!`;
          this.includeAllEntities = true;
        }
      });
    },

    inviteAllOrganizationMembers() {
      this.clearSelectedEntities();
      this.includeEntityTableOpen = false;

      this.$buefy.dialog.confirm({
        title: "Invite All Organization Members",
        confirmText: "Invite All Organization Members",
        type: "is-danger",
        hasIcon: true,
        message: `Are you sure you want to invite all organization members to this Challenge?`,
        onConfirm: async () => {
          this.updateFormData({ key: "includeAll", value: 1 });
          this.updateFormData({
            key: "includeType",
            value: "inviteOrganization"
          });
          this.includeAllEntitiesNote = `✔ Awesome, all Organization members will be invited to this challenge!`;
          this.includeAllEntities = true;
        }
      });
    },

    addOrgnizationToChallenge(organization) {
      this.clearSelectedEntities();
      this.includeEntityTableOpen = false;
      if (this.participantsTableOpen) {
        this.participantsTableOpen = false;
      }
      this.organizationId = organization.id;
      this.updateFormData({ key: "includeAll", value: 1 });
      this.updateFormData({
        key: "competitionOf",
        value: "Organization"
      });
      this.stageOrganization(organization);
      this.includeAllEntities = true;

      this.fetchOrganizationTeams({
        organizationId: organization.id,
        perPage: 2
      });
    },

    onSearch() {
      // defining this here, but is overwritten in the
      // mounted call.  Using debounce requires this
      // approach in order for it to work when
      // the component instance is available.
    }
  }
};
</script>

<style lang="sass" scoped>
.customize-button
    background-color: transparent;
    border-color: #2972F0;
    color: #2972F0;
.full-width-dropdown
    padding-top: 2px
    /deep/ button
      display: flex
      justify-content: space-between
</style>
